import config from '../_server/config'

/**
 * Interface defining the structure of a notification to be created
 * @template T - Generic type for additional data properties
 */
interface INotification<T> {
  /** Optional notification body text */
  body?: string
  /** Data payload including message metadata and control flags */
  data?: T & {
    /** Unique identifier for the message/notification */
    messageID: string
    /** Flag to make notification silent (no sound) */
    silent?: boolean
    /** Flag to completely skip showing browser notification */
    skipBrowserNotification?: boolean
  }
  /** Optional callback function to execute when notification is clicked */
  notificationHandler?: () => void
  /** Required notification title text */
  title: string
}

/**
 * Service interface for creating browser notifications
 */
export interface IBrowserNotificationService {
  /**
   * Generates a browser notification based on the provided event
   *
   * @param notification - Notification configuration object
   */
  create<T>(notification: INotification<T>): void
}

/**
 * Browser Notification Service
 *
 * Handles the creation and display of browser notifications with intelligent
 * multi-tab coordination to prevent duplicate notifications and sounds.
 *
 * Key Features:
 * - Leader election: Only one tab handles notifications at a time
 * - High-quality audio playback with fallback methods
 * - Automatic failover when leader tab closes
 * - Cross-tab coordination via localStorage
 */
export class BrowserNotificationService implements IBrowserNotificationService {
  /** HTML Audio element for playing notification sounds */
  notificationSound: HTMLAudioElement

  /** LocalStorage key for tracking which tab is the notification leader */
  private readonly LEADER_KEY = 'notification_leader_tab'

  /** Interval (ms) for leader tab heartbeat to prove it's still alive */
  private readonly HEARTBEAT_INTERVAL = 5000

  /** Unique identifier for this tab instance */
  private tabID: string

  /** Timer reference for the heartbeat interval */ // languagetools:disable-next-line
  private heartbeatInterval: NodeJS.Timeout | null = null

  /** Web Audio API context for high-quality audio processing */
  private audioContext: AudioContext | null = null

  constructor() {
    // Initialize the HTML Audio element with the notification sound URL
    this.notificationSound = new Audio(config.app.BROWSER_NOTIFICATION_SOUND)

    // Configure audio settings for optimal quality
    this.configureAudioElement()

    // Generate unique ID for this tab (timestamp + random string)
    this.tabID = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Set up high-quality audio context for better sound processing
    this.initializeAudioContext()

    // Start the leader election process to coordinate across tabs
    this.startLeaderElection()
  }

  /**
   * Configure HTML Audio element for optimal quality playback
   *
   * This method applies various settings to improve audio quality and
   * reduce browser-introduced artifacts like resampling or gain control.
   */
  private configureAudioElement(): void {
    // Preload the entire audio file to avoid delays and ensure smooth playback
    this.notificationSound.preload = 'auto'

    // Set volume to maximum to prevent browser from applying auto-gain reduction
    this.notificationSound.volume = 1.0

    // Extended audio element interface to access quality-related properties
    // These properties may not be supported in all browsers but improve quality when available
    const audioElement = this.notificationSound as HTMLAudioElement & {
      /** Hints browser to use high-quality audio decoding */
      preferredQuality?: string
      /** Prevents browser from altering audio pitch during time-stretching */
      preservesPitch?: boolean
    }

    // Prevent pitch changes during audio processing (reduces artifacts)
    if ('preservesPitch' in audioElement) {
      audioElement.preservesPitch = true
    }

    // Request high-quality audio decoding when supported
    if ('preferredQuality' in audioElement) {
      audioElement.preferredQuality = 'high'
    }
  }

  /**
   * Initialize Web Audio API context with high-quality settings
   *
   * Creates an AudioContext optimized for notification sound playback.
   * Also sets up automatic resumption when the tab becomes active or
   * user interacts with the page (required by browser audio policies).
   */
  private initializeAudioContext(): void {
    try {
      // Configure AudioContext for optimal audio quality
      const audioContextOptions: AudioContextOptions = {
        // Prioritize audio quality over low latency for notification sounds
        latencyHint: 'playback',
        // Use 48kHz sample rate (higher than default 44.1kHz) for better quality
        sampleRate: 48000
      }

      // Create the AudioContext with quality-optimized settings
      this.audioContext = new AudioContext(audioContextOptions)

      // Handle browser tab visibility changes
      // When tab becomes visible, resume suspended AudioContext
      document.addEventListener('visibilitychange', () => {
        if (this.audioContext && this.audioContext.state === 'suspended' && !document.hidden) {
          this.audioContext.resume()
        }
      })

      // Create function to resume AudioContext on user interaction
      // Required by browser policies that suspend audio without user interaction
      const resumeAudioContext = () => {
        if (this.audioContext && this.audioContext.state === 'suspended') {
          this.audioContext.resume()
        }
      }

      // Listen for user interactions to resume audio if suspended
      document.addEventListener('click', resumeAudioContext)
      document.addEventListener('keydown', resumeAudioContext)
    } catch (error) {
      // If AudioContext creation fails, log warning and continue without it
      console.warn('AudioContext initialization failed:', error)
      this.audioContext = null
    }
  }

  /**
   * Start the leader election process for cross-tab notification coordination
   *
   * Implements a simple leader election algorithm where only one tab across
   * all open instances handles notifications. This prevents duplicate sounds
   * and notifications when multiple tabs are open.
   *
   * The leader tab sends periodic heartbeats to prove it's still alive.
   * If a leader goes stale, other tabs can take over leadership.
   */
  private startLeaderElection(): void {
    // Immediately attempt to become leader
    this.attemptLeadership()

    // Set up periodic heartbeat to maintain leadership and detect stale leaders
    this.heartbeatInterval = setInterval(() => {
      this.attemptLeadership()
    }, this.HEARTBEAT_INTERVAL)

    // Clean up leadership when this tab is closing
    window.addEventListener('beforeunload', () => {
      // Stop the heartbeat timer
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval)
      }

      // If we're the current leader, remove leadership, so other tabs can take over
      const currentLeader = localStorage.getItem(this.LEADER_KEY)
      if (currentLeader === this.tabID) {
        localStorage.removeItem(this.LEADER_KEY)
      }
    })
  }

  /**
   * Attempt to become or maintain leadership for notifications
   *
   * Leadership determination logic:
   * 1. If no leader exists, become leader
   * 2. If current leader is stale (no heartbeat), take over
   * 3. If we're already leader, update our heartbeat timestamp
   */
  private attemptLeadership(): void {
    const currentLeader = localStorage.getItem(this.LEADER_KEY)
    const now = Date.now()

    if (!currentLeader) {
      // No current leader exists, so we become the leader
      localStorage.setItem(this.LEADER_KEY, `${this.tabID}:${now}`)
    } else {
      // Parse the leader data: "tabID:timestamp"
      const [leaderID, lastHeartbeat] = currentLeader.split(':')

      // Check if the current leader is stale (hasn't sent heartbeat recently)
      if (now - parseInt(lastHeartbeat) > this.HEARTBEAT_INTERVAL * 2) {
        // Leader is stale, take over leadership
        localStorage.setItem(this.LEADER_KEY, `${this.tabID}:${now}`)
      } else if (leaderID === this.tabID) {
        // We're already the leader, just update our heartbeat timestamp
        localStorage.setItem(this.LEADER_KEY, `${this.tabID}:${now}`)
      }
      // If neither condition is met, another tab is the active leader
    }
  }

  /**
   * Check if this tab is currently the notification leader
   *
   * @returns true if this tab should handle notifications, false otherwise
   */
  private isLeaderTab(): boolean {
    const currentLeader = localStorage.getItem(this.LEADER_KEY)

    // If no leader is stored, we're not the leader
    if (!currentLeader) return false

    // Extract the leader tab ID and compare with ours
    const [leaderID] = currentLeader.split(':')
    return leaderID === this.tabID
  }

  /**
   * Play notification sound with multi-method approach and error handling
   *
   * Uses a two-tier approach for maximum compatibility and quality:
   * 1. Pure HTML Audio (cleanest signal path, and best quality)
   * 2. AudioContext-managed playback (fallback with better state management)
   */
  private async playNotificationSound(): Promise<void> {
    try {
      // Method 1: Pure HTML Audio - often produces the highest quality
      // This bypasses most browser audio processing pipelines
      await this.playWithHTMLAudio()
    } catch (error) {
      console.warn('HTML Audio playback failed, trying with AudioContext:', error)

      try {
        // Method 2: AudioContext-managed playback as fallback
        // Better for handling suspended audio contexts and browser policies
        await this.playWithAudioContext()
      } catch (fallbackError) {
        console.warn('All playback methods failed:', fallbackError)
      }
    }
  }

  /**
   * Primary playback method using pure HTML Audio element
   *
   * This method provides the cleanest audio signal path with minimal
   * browser interference, often resulting in the best audio quality.
   */
  private async playWithHTMLAudio(): Promise<void> {
    // Reset playback position to the beginning of the audio file
    this.notificationSound.currentTime = 0

    // Initiate pure HTML Audio playback without AudioContext dependency
    // This avoids additional audio processing layers that can introduce artifacts
    const playPromise = this.notificationSound.play()

    // Handle both Promise-based and legacy play() implementations
    if (playPromise !== undefined) {
      await playPromise
    }
  }

  /**
   * Fallback playback method with AudioContext state management
   *
   * This method ensures the AudioContext is in the correct state before
   * attempting playback, handling browser audio policy restrictions.
   */
  private async playWithAudioContext(): Promise<void> {
    // Ensure AudioContext is active (not suspended by browser policies)
    if (this.audioContext && this.audioContext.state === 'suspended') {
      // Resume the AudioContext to enable audio playback
      await this.audioContext.resume()
    }

    // Reset audio playback position to start from the beginning
    this.notificationSound.currentTime = 0

    // Attempt to play the audio with AudioContext state management
    const playPromise = this.notificationSound.play()

    // Wait for playback to start successfully
    if (playPromise !== undefined) {
      await playPromise
    }
  }

  /**
   * Debug method to check notification leadership status
   * Run in console: debugServices.browserNotificationService.debugLeadershipStatus()
   */
  debugLeadershipStatus = () => {
    const currentLeader = localStorage.getItem(this.LEADER_KEY)
    console.log('=== Notification Leadership Debug ===')
    console.log('My Tab ID:', this.tabID)
    console.log('Current Leader:', currentLeader)
    console.log('Am I Leader?:', this.isLeaderTab())
    console.log('=== End Debug ===')

    return {
      currentLeader,
      isLeader: this.isLeaderTab(),
      myTabID: this.tabID
    }
  }

  /**
   * Debug method to test sound playback
   * Run in console: debugServices.browserNotificationService.debugSoundPlayback()
   */
  debugSoundPlayback = async () => {
    console.log('=== Sound Debug ===')
    console.log('AudioContext State:', this.audioContext?.state)
    console.log('Sound URL:', this.notificationSound.src)
    console.log('Is Leader:', this.isLeaderTab())

    if (this.isLeaderTab()) {
      console.log('Testing sound playback...')
      await this.playNotificationSound()
      console.log('Sound test completed')
    } else {
      console.log('Not leader tab - cannot test sound')
    }
    console.log('=== End Sound Debug ===')
  }

  /**
   * Create and display a browser notification with sound
   *
   * This is the main entry point for creating notifications. It handles:
   * - Metadata-based filtering (silent, skip flags)
   * - Leader election coordination (only leader tab shows notifications)
   * - Browser compatibility checking
   * - Notification creation with click handlers
   * - High-quality audio playback
   *
   * @param notification - Configuration object containing title, body, data, and handlers
   */
  create<T>(notification: INotification<T>): void {
    const { title, body, data, notificationHandler } = notification

    // Early exit: Skip notification if explicitly marked to be skipped or silent
    if (data?.skipBrowserNotification || data?.silent) {
      return
    }

    // Early exit: Only the leader tab should handle notifications
    // This prevents multiple notifications/sounds when multiple tabs are open
    if (!this.isLeaderTab()) {
      return
    }

    // Verify browser support and user permission for notifications
    if ('Notification' in window && window.Notification && Notification.permission === 'granted') {
      // Create the browser notification with optimized settings
      const newNotification = new Notification(title, {
        // Display the message body (empty string if not provided)
        body: body || '',
        // Attach metadata for potential future use
        data: data,
        // Set notification icon (if configured)
        icon: config.app.BROWSER_NOTIFICATION_ICON || '',
        // Use messageID as tag to replace duplicate notifications
        tag: data?.messageID || ''
      })

      // Set up click handler if provided
      if (notificationHandler) {
        newNotification.onclick = () => {
          // Bring the browser window to focus
          window.focus()
          // Execute the custom click handler
          notificationHandler()
          // Close the notification after handling click
          newNotification.close()
        }
      }

      // Play the notification sound using our high-quality audio system
      this.playNotificationSound()
    } else {
      // Log error for debugging notification issues
      console.error('Browser notifications not supported')

      // Additional context for debugging notification permission issues
      // Common scenarios:
      // - Internet Explorer or very old browsers lack Notification API
      // - Safari on iOS has restrictive notification policies
      // - User denied notification permission
      // - Corporate firewalls blocking notification features
    }
  }
}
